import { v4 as uuidv4 } from "uuid";
import CategoryServices from "../OrderingServices/CategoryService";
import OrganizationService from "../OrderingServices/OrganizationService";
import AdLogService from "./AdLogService";

const STORAGE_KEY = "ad_log_queue";
const DURATION_KEY = "ad_log_duration"; // store duration in ms
const LAST_SYNC_KEY = "ad_last_sync_time"; // store last sync timestamp

class AdLogQueueService {
  constructor(syncIntervalSeconds = 300) {
    this.isSyncing = false;
    this.syncInterval = syncIntervalSeconds * 1000; // convert to ms
    this.timer = null;
    this.macId = null;
    this.durationRefreshTimer = null;
    this.durationRefreshIntervalMs = 30 * 1000; // Fetch duration every 30 seconds

    this.startAutoSync();

    // Sync immediately when internet returns
    window.addEventListener("online", () => this.sync());
  }

  // Add log to queue
  enqueue(log) {
    const queue = this.getQueue();

    // Optional max queue protection
    if (queue.length > 1000) queue.shift();

    queue.push({
      ...log,
      id: uuidv4(),
      createdAt: new Date().toISOString(),
      synced: false,
      retryCount: 0,
    });

    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  }

  // Get all logs
  getQueue() {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  }

  // Remove successfully sent logs
  removeLogs(ids) {
    const remaining = this.getQueue().filter((log) => !ids.includes(log.id));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(remaining));
  }

  // Sync logs in batch to backend
  async sync() {
    if (this.isSyncing || !navigator.onLine) return;

    const lastSync = parseInt(localStorage.getItem(LAST_SYNC_KEY) || "0");
    const now = Date.now();
    const durationMs = parseInt(
      localStorage.getItem(DURATION_KEY) || this.syncInterval,
    );

    // Only sync if enough time has passed
    if (now - lastSync < durationMs) return;

    const queue = this.getQueue();
    console.log("🚀 ~ AdLogQueueService ~ sync ~ queue:", queue)
    if (queue.length === 0) return;

    
    this.isSyncing = true;

    try {
      const payload = queue.map((log) => ({
        adId: log.adId,
        macId: log.macId || this.macId,
        deviceName: log.deviceName,
        ipAddress: log.ipAddress,
        shownAt: log.shownAt,
        hiddenAt: log.hiddenAt,
        visibleSeconds: log.visibleSeconds,
        playedSeconds: log.playedSeconds,
        wasCompleted: log.wasCompleted,
        wasSkipped: log.wasSkipped,
        displayReason: log.displayReason,
        screenName: log.screenName,
        appVersion: log.appVersion,
        logDate: log.createdAt,
      }));

      await AdLogService.logAdImpression(payload);

      this.removeLogs(queue.map((l) => l.id));
      localStorage.setItem(LAST_SYNC_KEY, now.toString());

      console.log(
        `✅ Synced ${queue.length} ad logs at ${new Date(now).toISOString()}`,
      );
    } catch (error) {
      console.error("❌ Sync failed, will retry next interval", error);
      const updatedQueue = queue.map((log) => ({
        ...log,
        retryCount: log.retryCount + 1,
      }));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedQueue));
    }

    this.isSyncing = false;
  }

  // Start auto-sync timer
  startAutoSync() {
    this.timer = setInterval(() => this.sync(), 5000); // tick every 5s
    console.log(`🕒 Auto-sync started with dynamic duration`);
  }

  // Stop timer (if needed)
  stopAutoSync() {
    if (this.timer) clearInterval(this.timer);
  }

  // Optionally update interval dynamically
  setSyncInterval(seconds) {
    this.syncInterval = seconds * 1000;
    if (this.timer) {
      clearInterval(this.timer);
      this.startAutoSync();
    }
  }

  // Start continuous fetching of duration from API
  startDurationRefresh(macId) {
    this.macId = macId;

    // Fetch immediately on start
    this.refreshDurationFromApi();

    // Then fetch at intervals
    this.durationRefreshTimer = setInterval(
      () => this.refreshDurationFromApi(),
      this.durationRefreshIntervalMs,
    );

    console.log(
      `🔄 Started duration refresh timer (every 30 seconds) for macId: ${macId}`,
    );
  }

  // "145D346BDE3F",
  // Fetch duration from API and update sync interval if changed
  async refreshDurationFromApi() {
    if (!this.macId) return;

    try {
      const orgDevice = await OrganizationService.getLogDurationInMinutes(
        this.macId,
        // "145D346BDE3F",
      );
      if (orgDevice?.logDurationInMinutes) {
        const durationMs = orgDevice.logDurationInMinutes * 60 * 1000;
        localStorage.setItem(DURATION_KEY, durationMs.toString());
        console.log(`📡 Updated duration in localStorage: ${durationMs} ms`);
      }
    } catch (error) {
      console.error("❌ Failed to fetch duration from API:", error);
    }
  }

  // Stop duration refresh
  stopDurationRefresh() {
    if (this.durationRefreshTimer) {
      clearInterval(this.durationRefreshTimer);
      this.durationRefreshTimer = null;
      console.log(`🛑 Stopped duration refresh timer`);
    }
  }
}

// Export singleton
export default new AdLogQueueService(300); // default 300 seconds (5 minutes)
